import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { callSessions } from '@/api/callSessions';
import type { CallEventRow } from '@/api/callSessions';
import { ivrTrees } from '@/api/ivrTrees';
import { ivrNodes } from '@/api/ivrNodes';
import { cn } from '@/lib/cn';
import { formatRef } from '@/lib/formatRef';
import { X, Copy, Check, ChevronDown, ChevronRight, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DtmfKey } from '@/components/ivr/DtmfKey';
import { ResultChip } from './resultChip';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDuration(sec: number | null): string {
  if (sec === null) return '—';
  const m = Math.floor(sec / 60).toString().padStart(2, '0');
  const s = (sec % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

function fmtMoney(cents: number): string {
  return `$${(cents / 100).toFixed(4)}`;
}

function fmtLocalTime(iso: string): string {
  return new Date(iso).toLocaleString();
}

/** Offset from a base ISO string to another ISO string, formatted as mm:ss.SS */
function relTime(base: string, current: string): string {
  const diff = new Date(current).getTime() - new Date(base).getTime();
  if (diff < 0) return '00:00.00';
  const totalSec = diff / 1000;
  const m = Math.floor(totalSec / 60).toString().padStart(2, '0');
  const s = (totalSec % 60).toFixed(2).padStart(5, '0');
  return `${m}:${s}`;
}

// ── Human-readable event kind map ─────────────────────────────────────────────

const EVENT_LABELS: Record<string, string> = {
  enter_node: 'Entered node',
  exit_node: 'Exited node',
  dtmf: 'Key pressed',
  playback_dtmf: 'Key pressed',   // legacy kind — replaced by 'dtmf' + dtmf column
  playback_start: 'Playback started',
  playback_end: 'Playback ended',
  playback_error: 'Playback error',
  hangup: 'Call ended',
  answer: 'Call answered',
  timeout: 'Input timeout',
  auth_pass: 'Auth passed',
  auth_fail: 'Auth failed',
  transfer: 'Transferred',
  bookmark: 'Bookmark saved',
  points_awarded: 'Points awarded',
};

function humanKind(kind: string): string {
  return EVENT_LABELS[kind] ?? kind;
}

// ── Node-type dot color ───────────────────────────────────────────────────────

const NODE_COLORS: Record<string, string> = {
  playback: 'bg-blue-500',
  menu: 'bg-violet-500',
  transfer: 'bg-orange-500',
  sip_connect: 'bg-orange-500',
  jump: 'bg-amber-500',
  conference: 'bg-teal-500',
  voicemail: 'bg-teal-500',
  survey: 'bg-green-500',
  quiz: 'bg-green-500',
  payment: 'bg-emerald-500',
  shopping: 'bg-emerald-500',
  hangup: 'bg-red-400',
  dtmf: 'bg-slate-400',
};

function dotColor(eventKind: string, nodeType?: string): string {
  if (nodeType && NODE_COLORS[nodeType]) return NODE_COLORS[nodeType];
  if (eventKind === 'hangup') return 'bg-red-400';
  if (eventKind === 'dtmf') return 'bg-slate-400';
  if (eventKind === 'auth_fail') return 'bg-red-400';
  if (eventKind === 'auth_pass') return 'bg-green-500';
  if (eventKind === 'timeout') return 'bg-amber-400';
  return 'bg-slate-300';
}

// ── Node name resolver ────────────────────────────────────────────────────────

function NodeLabel({ nodeId }: { nodeId: string }) {
  const { data: node } = useQuery({
    queryKey: ['ivrNode', nodeId],
    queryFn: () => ivrNodes.get(nodeId),
    staleTime: 5 * 60 * 1000,
  });
  if (!node) return <span className="text-xs text-slate-500 font-mono">{nodeId.slice(0, 8)}…</span>;
  return (
    <span className="text-xs text-slate-500">
      <span className="font-mono mr-1">{formatRef(node.ref_number)}</span>
      <span>{node.name}</span>
    </span>
  );
}

// ── Single timeline event ─────────────────────────────────────────────────────

function TimelineEvent({
  event,
  baseTime,
  isLast,
}: {
  event: CallEventRow;
  baseTime: string;
  isLast: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const hasPayload = Object.keys(event.payload).length > 0;
  const isDtmf = event.event_kind === 'dtmf' || event.event_kind === 'playback_dtmf';
  // 'dtmf' events store the key in the dtmf column; legacy 'playback_dtmf' events
  // stored it in payload.digit.
  const dtmfDigit =
    event.dtmf ??
    ((event.payload as Record<string, unknown>)?.digit as string | undefined);

  // Resolve node type for dot color (from payload if available)
  const nodeType = (event.payload as Record<string, unknown>)?.node_type as string | undefined;

  return (
    <div className="flex gap-3">
      {/* Left rail: connector + dot */}
      <div className="flex flex-col items-center">
        <div
          className={cn(
            'mt-1 h-2.5 w-2.5 rounded-full shrink-0 ring-2 ring-background',
            dotColor(event.event_kind, nodeType),
          )}
        />
        {!isLast && <div className="flex-1 w-px bg-slate-200 mt-1" />}
      </div>

      {/* Content */}
      <div className="flex-1 pb-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            {/* Primary label */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-slate-800 leading-tight">
                {humanKind(event.event_kind)}
              </span>
              {isDtmf && dtmfDigit && <DtmfKey k={dtmfDigit} />}
            </div>
            {/* Node ref */}
            {event.node_id && (
              <div className="mt-0.5">
                <NodeLabel nodeId={event.node_id} />
              </div>
            )}
          </div>
          {/* Relative time */}
          <span className="font-mono text-[11px] text-slate-400 shrink-0 mt-0.5">
            {relTime(baseTime, event.created_at)}
          </span>
        </div>

        {/* Expand payload */}
        {hasPayload && (
          <div className="mt-1.5">
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="flex items-center gap-1 text-[11px] text-slate-400 hover:text-slate-600 transition-colors"
              aria-expanded={expanded}
              aria-label="Toggle payload"
            >
              {expanded ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
              payload
            </button>
            {expanded && (
              <pre className="mt-1.5 rounded-md bg-slate-950 border border-slate-800 px-3 py-2 text-[11px] text-slate-300 overflow-x-auto leading-relaxed">
                {JSON.stringify(event.payload, null, 2)}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Keys-pressed pills row ────────────────────────────────────────────────────

function KeysPressedRow({ keys }: { keys: string }) {
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {keys.split('').map((k, i) => (
        <DtmfKey key={i} k={k} />
      ))}
    </div>
  );
}

// ── Tree name link ────────────────────────────────────────────────────────────

function TreeLink({ treeId }: { treeId: string }) {
  const { data: tree } = useQuery({
    queryKey: ['ivrTree', treeId],
    queryFn: () => ivrTrees.get(treeId),
    staleTime: 5 * 60 * 1000,
  });
  if (!tree) return <span className="font-mono text-xs text-slate-400">{treeId.slice(0, 8)}…</span>;
  return (
    <a
      href={`/ivr/trees/${tree.id}`}
      className="text-sm text-primary hover:underline"
    >
      {tree.name}
    </a>
  );
}

// ── Copy button ───────────────────────────────────────────────────────────────

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }
  return (
    <Button variant="outline" size="sm" onClick={copy} className="text-xs">
      {copied ? <Check className="h-3.5 w-3.5 mr-1.5" /> : <Copy className="h-3.5 w-3.5 mr-1.5" />}
      {copied ? 'Copied' : 'Copy session ID'}
    </Button>
  );
}

// ── Main drawer ───────────────────────────────────────────────────────────────

interface Props {
  sessionId: string;
  onClose: () => void;
}

export function CallSessionDrawer({ sessionId, onClose }: Props) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['callSession', sessionId],
    queryFn: () => callSessions.get(sessionId),
  });

  const session = data?.session;
  const events = data?.events ?? [];
  const isLive = session && !session.ended_at;

  return (
    /* Backdrop */
    <div className="fixed inset-0 z-40 flex justify-end" aria-modal="true" role="dialog" aria-label="Call session detail">
      <div className="fixed inset-0 bg-black/20" onClick={onClose} aria-hidden />
      {/* Slide-over panel */}
      <div className="relative z-50 flex h-full w-full max-w-[520px] flex-col bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div className="flex items-center gap-2 min-w-0">
            <Phone className="h-4 w-4 text-slate-400 shrink-0" />
            {session ? (
              <span className="text-sm text-slate-700 truncate">
                {/* CNAM display name in front of the raw number when
                    available (migration 0088). */}
                {session.caller_display_name ? (
                  <>
                    <span className="font-medium">{session.caller_display_name}</span>
                    <span className="ml-1.5 font-mono text-[11px] text-slate-500">
                      {session.from_number}
                    </span>
                  </>
                ) : (
                  <span className="font-mono">{session.from_number ?? '—'}</span>
                )}
                <span className="mx-2 text-slate-300">→</span>
                <span className="font-mono">{session.to_number ?? '—'}</span>
              </span>
            ) : (
              <span className="text-sm text-slate-400">Loading…</span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
            aria-label="Close drawer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
          {isLoading && (
            <div className="text-sm text-slate-400">Loading session…</div>
          )}
          {error && (
            <div role="alert" className="text-sm text-red-500">
              {error instanceof Error ? error.message : 'Failed to load session.'}
            </div>
          )}

          {session && (
            <>
              {/* Summary card — calmer label style than the previous
                  SHOUTY uppercase + wide-tracking. Title-case + softer
                  weight reads more naturally; the data underneath is
                  the focus, not the field labels. */}
              <div className="rounded-xl border border-border bg-slate-50/60 px-4 py-4 space-y-3.5">
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                  {/* Started */}
                  <div>
                    <div className="text-[11px] font-medium text-slate-500">Started</div>
                    <div className="mt-0.5 text-slate-700">{fmtLocalTime(session.started_at)}</div>
                  </div>
                  {/* Duration */}
                  <div>
                    <div className="text-[11px] font-medium text-slate-500">Duration</div>
                    <div className="mt-0.5 font-mono tabular-nums text-slate-700">
                      {isLive ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                          </span>
                          <span className="text-emerald-600 font-semibold text-xs">Live</span>
                        </span>
                      ) : (
                        fmtDuration(session.duration_sec)
                      )}
                    </div>
                  </div>
                  {/* Tree */}
                  {session.tree_id && (
                    <div>
                      <div className="text-[11px] font-medium text-slate-500">Tree</div>
                      <div className="mt-0.5">
                        <TreeLink treeId={session.tree_id} />
                      </div>
                    </div>
                  )}
                  {/* Result — use the same friendly chip the table uses
                      so the raw hangup_cause (caller_hangup, etc.)
                      doesn't leak through to the customer view. */}
                  {session.hangup_cause && (
                    <div>
                      <div className="text-[11px] font-medium text-slate-500">Result</div>
                      <div className="mt-1">
                        <ResultChip hangupCause={session.hangup_cause} />
                      </div>
                    </div>
                  )}
                  {/* Cost */}
                  {session.cost_cents > 0 && (
                    <div>
                      <div className="text-[11px] font-medium text-slate-500">Cost</div>
                      <div className="mt-0.5 font-mono tabular-nums text-slate-700">
                        {fmtMoney(session.cost_cents)}
                      </div>
                    </div>
                  )}
                </div>

                {/* Keys pressed */}
                {session.keys_pressed && session.keys_pressed.length > 0 && (
                  <div className="pt-1">
                    <div className="text-[11px] font-medium text-slate-500 mb-1.5">Keys pressed</div>
                    <KeysPressedRow keys={session.keys_pressed} />
                  </div>
                )}
              </div>

              {/* Events timeline */}
              <div>
                <h2 className="text-[11px] font-semibold text-slate-500 mb-3">
                  Events timeline
                </h2>
                {events.length === 0 ? (
                  <p className="text-sm text-slate-400">No events recorded.</p>
                ) : (
                  <div>
                    {events.map((ev, i) => (
                      <TimelineEvent
                        key={ev.id}
                        event={ev}
                        baseTime={session.started_at}
                        isLast={i === events.length - 1}
                      />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-border px-5 py-3">
          <CopyButton text={sessionId} />
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
